import { useCallback, useMemo, useState } from 'react';

import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DataTableEndlessPaginated } from '@/components/data-table/data-table-endless-paginated';
import { DataTableFilters } from '@/components/data-table/filters-v1/data-table-filters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { displayToastSuccess } from '@/helpers';
import { useDataTable } from '@/hooks/use-data-table';
import { useFilterValues } from '@/hooks/use-filter-values';
import { TableIds } from '@/lib/constants/table-ids';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { ErrorState } from '@/modules/common/components/organisms/error-state';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { SettingsRoutes } from '@/router/routes';

import { getTimeOffChangesTabFiltersConfig } from '../../constants/time-off-changes-filters-config';
import { getTimeOffChangesColumns } from '../../constants/time-off-changes-table-columns';
import { useDeleteTimeOffTypeChange } from '../../hooks/time-offs/use-delete-time-off-type-change';
import { useTimeOffTypeChanges } from '../../hooks/time-offs/use-time-off-type-changes';
import { TimeOffTypeChangeLogEntry } from '../../types/time-off-changes';

interface TimeOffChangesTabProps {
  typeId: number;
}

export const TimeOffChangesTab = ({ typeId }: TimeOffChangesTabProps) => {
  const { t } = useTranslation('authenticated.settings');
  const navigate = useNavigate();
  const { getErrorMessage, displayToastErrorFromApiWithFallback } = useApiError();
  const [changeToDelete, setChangeToDelete] = useState<TimeOffTypeChangeLogEntry | null>(null);
  const deleteMutation = useDeleteTimeOffTypeChange();

  const filterConfig = useMemo(() => getTimeOffChangesTabFiltersConfig(), []);
  const { values: appliedFilters } = useFilterValues(filterConfig);

  const {
    data: changesData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useTimeOffTypeChanges({
    typeId,
    filters: appliedFilters,
    enabled: !!typeId,
  });

  const handleDeleteClick = useCallback((change: TimeOffTypeChangeLogEntry) => {
    setChangeToDelete(change);
  }, []);

  const handleConfirmDelete = useCallback(
    async (closeModal: () => void) => {
      if (!changeToDelete) return;

      try {
        await deleteMutation.mutateAsync({ typeId, changeId: changeToDelete.id });
        displayToastSuccess(t('pages.timeOff.details.changesTab.deleteModal.deleteSuccess'));
        closeModal();
        setChangeToDelete(null);
      } catch (error) {
        displayToastErrorFromApiWithFallback(
          error,
          'pages.timeOff.details.changesTab.deleteModal.deleteError'
        );
      }
    },
    [changeToDelete, deleteMutation, displayToastErrorFromApiWithFallback, t, typeId]
  );

  const columns = useMemo(
    () =>
      getTimeOffChangesColumns({
        onDeleteClick: handleDeleteClick,
        isDeleting: deleteMutation.isPending,
      }),
    [deleteMutation.isPending, handleDeleteClick]
  );

  const { table, saveColumnSizing } = useDataTable<TimeOffTypeChangeLogEntry>({
    data: changesData?.items || [],
    columns,
    tableId: TableIds.TIME_OFF_TYPE_CHANGES,
    getRowId: row => `${row.id}`,
  });

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (error) {
    return (
      <ErrorState
        error={getErrorMessage(error)}
        className="min-h-[400px]"
        actions={[
          {
            label: t('common:retry'),
            onClick: () => refetch(),
            variant: 'default',
            icon: <RefreshCw className="h-4 w-4" />,
          },
          {
            label: t('common:back'),
            onClick: () => navigate(SettingsRoutes.TimeOff),
            variant: 'outline',
          },
        ]}
      />
    );
  }

  return (
    <>
      <Card className="flex max-h-[calc(100vh-100px)] flex-col">
        <CardHeader className="shrink-0">
          <CardTitle>{t('pages.timeOff.details.changesTab.changeHistory')}</CardTitle>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="shrink-0">
            <DataTableFilters filtersConfig={filterConfig} className="flex flex-wrap gap-2" />
          </div>

          <p className="shrink-0 text-xs text-muted-foreground">
            {changesData?.pagination?.total ?? 0}{' '}
            {t('pages.timeOff.details.changesTab.changes').toLowerCase()}
          </p>

          <DataTableEndlessPaginated
            table={table}
            isLoading={isLoading || isFetchingNextPage}
            hasMore={hasNextPage || false}
            onLoadMore={handleLoadMore}
            stickyHeader
            tableId={TableIds.TIME_OFF_TYPE_CHANGES}
            onResizeEnd={saveColumnSizing}
          />
        </CardContent>
      </Card>

      <ConfirmationModal
        open={!!changeToDelete}
        onOpen={() => {}}
        onClose={() => setChangeToDelete(null)}
        title={t('pages.timeOff.details.changesTab.deleteModal.confirmTitle')}
        message={t('pages.timeOff.details.changesTab.deleteModal.confirmMessage')}
        confirmText={t('pages.timeOff.details.changesTab.deleteModal.delete')}
        confirmVariant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
